import { toMajorUnits, type CurrencyCode } from '@poweredbypercent/utility-core'

export type ValidationRuleOperator = '<' | '<=' | '>' | '>=' | '='

export type CandidateFieldComparisonRule = {
  type: 'candidate_field_comparison'
  operator: ValidationRuleOperator
  candidateField: string
  percentage?: number
  errorMessage?: string
}

export type ValueComparisonRule = {
  type: 'value_comparison'
  operator: ValidationRuleOperator
  value: number
  errorMessage?: string
}

export type ValidationRule = CandidateFieldComparisonRule | ValueComparisonRule

export type CandidateData = Record<string, unknown>

const isMoneyObject = (
  value: unknown
): value is { amount: number; currency: string } =>
  typeof value === 'object' &&
  value !== null &&
  'amount' in value &&
  'currency' in value

const resolveCandidateFieldValue = (
  candidateData: CandidateData,
  field: string
): number | undefined => {
  const raw = candidateData[field]
  if (raw == null) return undefined

  if (typeof raw === 'number') return Number.isNaN(raw) ? undefined : raw

  if (isMoneyObject(raw)) {
    // Money objects from the API store amounts in minor units (cents) — convert to major
    // units (dollars) so comparisons match the form input which is in major units
    try {
      const converted = toMajorUnits(
        Number(raw.amount),
        raw.currency as CurrencyCode
      )
      return Number.isNaN(converted) ? undefined : converted
    } catch {
      const num = Number(raw.amount)
      return Number.isNaN(num) ? undefined : num
    }
  }

  const num = Number(raw)
  return Number.isNaN(num) ? undefined : num
}

const compareValues = (
  answerValue: number,
  operator: ValidationRuleOperator,
  referenceValue: number
): boolean => {
  switch (operator) {
    case '<':
      return answerValue < referenceValue
    case '<=':
      return answerValue <= referenceValue
    case '>':
      return answerValue > referenceValue
    case '>=':
      return answerValue >= referenceValue
    case '=':
      return Math.round(answerValue * 100) === Math.round(referenceValue * 100)
    default:
      return false
  }
}

export const evaluateValidationRules = (
  value: string,
  rules: ValidationRule[],
  candidateData: CandidateData
): string | null => {
  const answerValue = Number(value)
  if (Number.isNaN(answerValue)) {
    return 'Value must be a number'
  }

  for (const rule of rules) {
    let refValue: number
    // The value substituted for the `$value` placeholder in errorMessage: the raw
    // candidate field value (e.g. the actual operating budget, before any percentage
    // adjustment) for candidate comparisons, or the fixed target for value comparisons.
    let placeholderValue: number

    if (rule.type === 'candidate_field_comparison') {
      const rawRefValue = resolveCandidateFieldValue(
        candidateData,
        rule.candidateField
      )
      if (rawRefValue === undefined) continue

      refValue =
        rule.percentage != null
          ? rawRefValue * (rule.percentage / 100)
          : rawRefValue
      placeholderValue = rawRefValue
    } else if (rule.type === 'value_comparison') {
      refValue = rule.value
      placeholderValue = rule.value
    } else {
      continue
    }

    if (!compareValues(answerValue, rule.operator, refValue)) {
      const message =
        rule.errorMessage ?? `Value must be ${rule.operator} ${refValue}`
      return message.replace(/\$value\b/g, String(placeholderValue))
    }
  }

  return null
}
